import { useEffect, useState } from "react";
import SectionHead from "./SectionHead";
import { resolveLifeArea } from "../lifeAreas";
import { shortDate } from "../format";
import { SECTION_COPY, ACTIVE_COPY } from "../copy";
import { viewAllRoute } from "../lifecycle";

/**
 * ACTIVE_DIALOGUES (Figma 9154:8266) — the member's Dialogues in progress.
 *
 * Items are NOT UniversalDialogueCards (guide §6.1 rule 5): `{
 * dialogue_instance_id, dialogue_id?, title, state, cta, image_url?,
 * awaiting_followup? }`. Rendered in array order — under `explore_v26_5` the
 * server already puts an outstanding follow-up first, then the soonest
 * commitment, then recency.
 *
 * Tapping the card (image or title) opens the Dialogue Detail by
 * `dialogue_id` — the template id, or the research id (CDLG-…) for a Research
 * instance, as the Active Dialogues list does. A card without one has no
 * Detail to open and only offers Continue.
 *
 * Continue always goes by `dialogue_instance_id`: starting by template would
 * open a second instance instead of resuming this one.
 *
 * Not drawn, by contract: the "3 Conversations" count (C1). The area line and
 * "Last conversation" appear only once aris-ai sends `display_area_key` and
 * `last_activity_at` (§7 E2, E3).
 *
 * "View All" (Figma 11115:27005) opens the Active Dialogues list: the
 * section's `view_all_href` when it is an in-app path, else the list route.
 * Paused and archived Dialogues leave the rail on their own — the server only
 * lists ACTIVE here.
 */

function ActiveMedia({ src }) {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    setOk(false);
  }, [src]);
  return (
    <span className="ex-active__media">
      {src && (
        <img
          src={src}
          alt=""
          className={ok ? "ex-active__img ex-active__img--ready" : "ex-active__img"}
          onLoad={() => setOk(true)}
          onError={() => setOk(false)}
        />
      )}
    </span>
  );
}

function ActiveDialoguesSection({ section, onContinue, onOpen, onViewAll }) {
  const items = (section?.items || []).filter((item) => item?.dialogue_instance_id);
  if (items.length === 0) return null;

  const c = SECTION_COPY.ACTIVE_DIALOGUES;

  return (
    <section className="ex-section">
      <SectionHead
        title={c.title}
        subtitle={c.subtitle}
        action={
          onViewAll
            ? { label: c.viewAll, onClick: () => onViewAll(viewAllRoute(section?.view_all_href)) }
            : null
        }
      />
      <div className="ex-rail ex-rail--active">
        {items.map((item) => {
          const area = item.display_area_key
            ? resolveLifeArea({ key: item.display_area_key })
            : null;
          const last = shortDate(item.last_activity_at);
          const openable = Boolean(onOpen && item.dialogue_id);
          return (
            <article
              key={item.dialogue_instance_id}
              className={openable ? "ex-active ex-active--openable" : "ex-active"}
              onClick={openable ? () => onOpen(item) : undefined}
            >
              <ActiveMedia src={item.image_url} />
              <div className="ex-active__body">
                <div className="ex-active__text">
                  <h3 className="ex-active__title">
                    {openable ? (
                      <button
                        type="button"
                        className="ex-active__open"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(item);
                        }}
                      >
                        {item.title}
                      </button>
                    ) : (
                      item.title
                    )}
                  </h3>
                  {area && <p className="ex-active__meta">{area.title}</p>}
                </div>
                {item.awaiting_followup && (
                  <p className="ex-active__followup">{ACTIVE_COPY.followup}</p>
                )}
                {last && (
                  <p className="ex-active__meta">
                    {ACTIVE_COPY.lastConversation} {last}
                  </p>
                )}
                <button
                  type="button"
                  className="ex-active__cta"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContinue?.(item);
                  }}
                >
                  {ACTIVE_COPY.cta}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default ActiveDialoguesSection;
